import { useState, useEffect, useRef } from 'react';
import { DiwaliBadge } from '../components/DiwaliBadge';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { useCart } from '../contexts/CartContext';
import { PaddleStage, viewsFromImages } from '../components/PaddleStage';
import { ProductWordmark } from '../components/ProductWordmark';
import NotifyMeForm from '../components/NotifyMeForm';
import { LaunchCountdown, LaunchedBanner } from '../components/LaunchCountdown';
import { useLaunchTimer } from '../hooks/useLaunchTimer';
import { Plus, Minus, ShoppingCart, ArrowLeft, ArrowRight, Clock, ChevronDown } from 'lucide-react';
import {
  CoreIcon, ThicknessIcon, WeightIcon, PaddleLengthIcon, HandleLengthIcon,
  SurfaceWidthIcon, SurfaceLengthIcon, GripCircumferenceIcon,
} from '../components/PaddleSpecIcons';
import { Seo } from '../components/Seo';
import { ProductReviews } from '../components/reviews/PlayerReviews';
import { TrustBadges } from '../components/TrustBadges';
import { gaEvent, gaCartItem } from '../lib/analytics';
import { fbTrackDual } from '../lib/pixel';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { AmazonBuyButton } from '../components/AmazonBuy';
import { PADDLES, TECH_FAQ } from '../data/technicals';
import { PlayProfile } from '../components/technicals/PlayProfile';
import { COMPANY } from '../data/legalContent';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE = 'https://dyssc.com';
const TORAY_BLUE = '#004098';
const TORAY_LOGO = `${BACKEND_URL}/api/static/brand/toray.png`;

// Keyword descriptor per product (drives H1, title, FAQ).
const SEO_TYPE = {
  hexcore: 'Carbon Fiber Pickleball Paddle',
  profoam: 'Foam Core Pickleball Paddle',
};

// Per-paddle accent theme — class strings kept literal so Tailwind JIT emits them
const THEMES = {
  hexcore: {
    accent: '#E02128',
    eyebrow: 'Flagship · USA Pickleball Approved',
    text: 'text-[#E02128]',
    badge: 'text-[#E02128] bg-[#E021281a] border-[#E0212840]',
    grad: 'from-[#E02128] via-[#FF5A60] to-[#E02128] shadow-[0_4px_16px_rgba(224,33,40,0.35)]',
    btn: 'bg-[#E02128]',
    deco: 'hover:decoration-[#E02128]',
    faqs: [TECH_FAQ[0], TECH_FAQ[2], TECH_FAQ[3], TECH_FAQ[5]],
  },
  profoam: {
    accent: '#1E6FD9',
    eyebrow: 'New · USA Pickleball Approved',
    text: 'text-[#1E6FD9]',
    badge: 'text-[#1E6FD9] bg-[#1E6FD91a] border-[#1E6FD940]',
    grad: 'from-[#1E6FD9] via-[#4F97FF] to-[#1E6FD9] shadow-[0_4px_16px_rgba(30,111,217,0.35)]',
    btn: 'bg-[#1E6FD9]',
    deco: 'hover:decoration-[#1E6FD9]',
    faqs: [TECH_FAQ[1], TECH_FAQ[4], TECH_FAQ[5], TECH_FAQ[6]],
  },
};

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const timer = useLaunchTimer();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewAgg, setReviewAgg] = useState({ avg: 0, count: 0 });
  const [showBreakup, setShowBreakup] = useState(false);
  const buyBtnRef = useRef(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
        const p = response.data;
        gaEvent('view_item', {
          currency: 'INR',
          value: p.priceINR,
          items: [gaCartItem({ productId: p.slug, name: p.name, priceINR: p.priceINR }, 1)],
        });
        fbTrackDual('ViewContent', {
          content_type: 'product',
          content_ids: [String(p.slug)],
          content_name: p.name,
          currency: 'INR',
          value: p.priceINR,
          contents: [{ id: String(p.slug), quantity: 1, item_price: p.priceINR }],
        });
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  useEffect(() => {
    axios.get(`${API}/reviews/product/${slug}`)
      .then(({ data }) => {
        setReviews(data.reviews || []);
        setReviewAgg(data.aggregate || { avg: 0, count: 0 });
      })
      .catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9FA] flex items-center justify-center">
        <div className="text-[#52525B] text-lg">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F9F9FA] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-[#111214] text-2xl mb-4">Product not found</h2>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  const isLive = product.live === true;
  const theme = THEMES[product.slug] || THEMES.hexcore;

  // ----- SEO -----
  const seoType = SEO_TYPE[product.slug] || 'Pickleball Paddle';
  const canonical = `${SITE}/product/${product.slug}`;
  const ogImg = (product.images || []).find((i) => !i.endsWith('.mp4')) || undefined;
  const seoTitle = `${product.name} – ${seoType} (USAPA) | DYSSC India`;
  const seoDesc = `${product.name}: a USA Pickleball (USAPA) approved ${seoType.toLowerCase()} with a ${product.specs.thickness} ${product.specs.core} core and ${product.specs.face} face. Buy online in India with secure checkout.`;
  const faqs = [
    { q: `Is the ${product.name} USAPA approved?`, a: `Yes. The ${product.name} is USA Pickleball (USAPA) approved and tournament-legal.` },
    { q: `What is the ${product.name} made of?`, a: `It pairs a ${product.specs.thickness} ${product.specs.core} core with a ${product.specs.face} surface and weighs ${product.specs.weight}.` },
    ...theme.faqs,
    { q: `Do you deliver the ${product.name} across India?`, a: `Yes — DYSSC ships pickleball paddles across India with secure online payment and fast dispatch.` },
  ];
  const jsonLd = [
    // Product schema only for live products: a PreOrder offer without a price is
    // an automatic critical error in Google's Product snippets report
    ...(!isLive ? [] : [{
      '@context': 'https://schema.org/', '@type': 'Product',
      name: `${product.name} ${seoType}`,
      image: (product.images || []).filter((i) => !i.endsWith('.mp4')).map((i) => (i.startsWith('http') ? i : `https://dyssc.com${i}`)),
      description: product.description,
      brand: { '@type': 'Brand', name: 'DYSSC' },
      material: product.specs.face,
      category: 'Pickleball Paddle',
      sku: product.slug,
      offers: {
        '@type': 'Offer', url: canonical, priceCurrency: 'INR',
        price: product.priceINR,
        priceValidUntil: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
        availability: product.in_stock === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition', seller: { '@type': 'Organization', name: 'DYSSC' },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: { '@type': 'MonetaryAmount', value: 0, currency: 'INR' },
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'IN' },
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
            transitTime: { '@type': 'QuantitativeValue', minValue: 2, maxValue: 5, unitCode: 'DAY' },
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'IN',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
          merchantReturnLink: 'https://dyssc.com/shipping-returns',
          description: 'Returns accepted only for verified manufacturing defects (unused, original packaging). See policy.',
        },
      },
      ...(reviewAgg.count > 0 ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: reviewAgg.avg,
          reviewCount: reviewAgg.count,
          bestRating: 5, worstRating: 1,
        },
        review: reviews.slice(0, 10).map((r) => ({
          '@type': 'Review',
          reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
          author: { '@type': 'Person', name: r.name },
          ...(r.body ? { reviewBody: r.body } : {}),
        })),
      } : {}),
    }]),
    {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
        { '@type': 'ListItem', position: 2, name: 'Pickleball Paddles', item: `${SITE}/#shop` },
        { '@type': 'ListItem', position: 3, name: product.name, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#111214]">
      <Seo title={seoTitle} description={seoDesc} canonical={canonical} image={ogImg} jsonLd={jsonLd} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-10 md:pt-8 md:pb-12">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#52525B] hover:text-[#111214] transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Shop
        </button>

        {/* Product header — brand + wordmark, reads "DYSSC HEXCORE" */}
        <div className="mb-2 md:mb-4" data-testid="product-header">
          <p className={`font-mono text-[11px] uppercase tracking-[0.3em] ${theme.text} font-bold mb-2`}>
            {theme.eyebrow}
          </p>
          <h1 data-testid="product-title">
            <span className="sr-only">{product.name} — USA Pickleball approved {seoType.toLowerCase()}</span>
            <img
              src={`${API}/static/brand/dyssc_wordmark_dark.png`}
              alt=""
              aria-hidden="true"
              className="h-4 md:h-5 w-auto"
            />
            <ProductWordmark
              slug={product.slug}
              name={product.name}
              imgClassName="h-12 md:h-16 mt-1"
              fallbackClassName="font-display text-4xl md:text-5xl text-[#111214]"
            />
          </h1>
          <p className="text-base md:text-xl text-[#52525B] mt-1.5">
            {product.tagline}
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-3 md:gap-8 items-start">
          {/* Interactive studio stage — matches homepage showcase */}
          <div className="md:col-span-6">
            {product.images.length > 1 ? (
              <PaddleStage
                views={viewsFromImages(product.images)}
                alt={`${product.name} pickleball paddle`}
                showHint
                accent={theme.accent}
                heightClass="h-[400px] md:h-[560px]"
              />
            ) : product.images[0].endsWith('.mp4') ? (
              <video
                src={product.images[0]}
                autoPlay
                loop
                muted
                playsInline
                className="w-full max-w-[320px] mx-auto rounded-2xl"
              />
            ) : (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full max-w-[320px] mx-auto rounded-2xl"
              />
            )}
          </div>

          {/* Product Details — wider column */}
          <div className="md:col-span-6 space-y-4">
            <p className="text-sm text-[#52525B] leading-relaxed max-w-xl">
              The {product.name} is a premium, USA Pickleball–approved {seoType.toLowerCase()} from DYSSC — engineered in India with a {product.specs.thickness} {product.specs.core} core and a {product.specs.face} surface for lighter weight and precision.
            </p>

            <Separator className="bg-[#E4E4E7]" />

            {isLive ? (
              <div>
                <p className="text-sm text-[#52525B] mb-2 uppercase tracking-wider flex items-center gap-2">
                  Price (incl. GST)
                  {product.in_stock === false && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest border ${theme.badge}`} data-testid="sold-out-badge-detail">
                      SOLD OUT
                    </span>
                  )}
                </p>
                <div className="flex items-end gap-3 flex-wrap" data-testid="product-price-block">
                  <p className="font-mono text-4xl md:text-5xl text-[#111214]" data-testid="product-price">
                    ₹{product.priceINR.toLocaleString('en-IN')}
                  </p>
                  {product.compareAtINR > product.priceINR && (
                    <>
                      <span className="font-mono text-xl text-[#A1A1AA] line-through mb-1.5" data-testid="product-compare-price">
                        ₹{product.compareAtINR.toLocaleString('en-IN')}
                      </span>
                      <span className={`mb-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wider text-white bg-gradient-to-r ${theme.grad} bg-[length:200%_100%] animate-[gradient-x_2.5s_linear_infinite]`} data-testid="product-discount-badge">
                        {product.discountPercent}% OFF
                      </span>
                    </>
                  )}
                  <DiwaliBadge accent={theme.accent} className="mb-2" testId="diwali-offer-badge-detail" />
                </div>

                {/* Price breakup — GST-inclusive working, revealed on click */}
                <button
                  type="button"
                  onClick={() => setShowBreakup((v) => !v)}
                  className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#111214] underline underline-offset-4 decoration-[#E4E4E7] ${theme.deco} transition-colors`}
                  data-testid="price-breakup-toggle"
                  aria-expanded={showBreakup}
                >
                  Price breakup
                  <ChevronDown className={`w-4 h-4 transition-transform ${showBreakup ? 'rotate-180' : ''}`} />
                </button>

                {showBreakup && (() => {
                  const hasDiscount = product.compareAtINR > product.priceINR;
                  const base = product.basePriceINR || Math.round((product.compareAtINR || product.priceINR) / 1.18);
                  const discAmt = hasDiscount ? Math.round((base * (product.discountPercent || 0)) / 100) : 0;
                  const taxable = base - discAmt;
                  const gst = product.priceINR - taxable;
                  return (
                    <div
                      className="mt-3 max-w-sm rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] p-4 text-sm space-y-2"
                      data-testid="price-breakup-panel"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[#52525B]">Base price (excl. GST)</span>
                        <span className="font-mono text-[#111214]" data-testid="breakup-base">₹{base.toLocaleString('en-IN')}</span>
                      </div>
                      {hasDiscount && (
                        <>
                          <div className={`flex items-center justify-between ${theme.text}`}>
                            <span>Discount ({product.discountPercent}% OFF)</span>
                            <span className="font-mono" data-testid="breakup-discount">− ₹{discAmt.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[#52525B]">Taxable value</span>
                            <span className="font-mono text-[#111214]" data-testid="breakup-taxable">₹{taxable.toLocaleString('en-IN')}</span>
                          </div>
                        </>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-[#52525B]">GST (18%)</span>
                        <span className="font-mono text-[#111214]" data-testid="breakup-gst">+ ₹{gst.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#E4E4E7] font-semibold text-[#111214]">
                        <span>You pay (incl. GST)</span>
                        <span className="font-mono" data-testid="breakup-total">₹{product.priceINR.toLocaleString('en-IN')}</span>
                      </div>
                      <p className="text-[11px] text-[#A1A1AA] pt-1">Inclusive of all taxes · GST @ 18% (HSN 9506).</p>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div data-testid="product-coming-soon">
                <p className="text-sm text-[#52525B] mb-2 uppercase tracking-wider">Availability</p>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111214] text-white">
                  <Clock className={`w-4 h-4 ${theme.text}`} />
                  <span className="font-bold uppercase tracking-[0.2em] text-sm">Coming Soon</span>
                </div>
              </div>
            )}

            <Separator className="bg-[#E4E4E7]" />

            <div>
              <h3 className="font-heading font-semibold text-lg mb-2 text-[#111214]">Description</h3>
              <p className="text-[#52525B] leading-relaxed">{product.description}</p>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-lg mb-3 text-[#111214]">Specifications</h3>

              {/* Toray T700 carbon fibre badge — matches the official Toray blue */}
              {product.specs.face && (
                <div
                  className="mb-4 flex items-center gap-3 rounded-lg p-3 border"
                  style={{ borderColor: TORAY_BLUE, backgroundColor: 'rgba(0,64,152,0.06)' }}
                  data-testid="toray-badge"
                >
                  <img src={TORAY_LOGO} alt="Toray" className="h-5 w-auto shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#111214]">Toray T700 Carbon Fibre Surface</p>
                    <p className="text-xs text-[#52525B]">Aerospace-grade carbon fibre for power, control &amp; durability.</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2.5" data-testid="spec-grid">
                {[
                  { label: 'Core', value: product.specs.core, Icon: CoreIcon },
                  { label: 'Thickness', value: product.specs.thickness, Icon: ThicknessIcon },
                  { label: 'Weight', value: product.specs.weight, Icon: WeightIcon },
                  { label: 'Paddle Length', value: product.specs.paddle_length, Icon: PaddleLengthIcon },
                  { label: 'Handle Length', value: product.specs.handle_length, Icon: HandleLengthIcon },
                  { label: 'Surface Width', value: product.specs.surface_width, Icon: SurfaceWidthIcon },
                  { label: 'Surface Length', value: product.specs.surface_length, Icon: SurfaceLengthIcon },
                  { label: 'Grip Circumference', value: product.specs.grip, Icon: GripCircumferenceIcon },
                ]
                  .filter((s) => s.value)
                  .map((s) => (
                    <div
                      key={s.label}
                      className="p-3 rounded-lg bg-[#F4F4F5] border border-[#E4E4E7]"
                      data-testid={`spec-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <s.Icon className={`w-5 h-5 ${theme.text} shrink-0`} />
                        <p className="text-sm text-[#52525B]">{s.label}</p>
                      </div>
                      <p className="font-semibold text-[#111214]">{s.value}</p>
                    </div>
                  ))}
              </div>

              {PADDLES[product.slug] && (
                <div className="mt-6 rounded-xl border border-[#E4E4E7] bg-white p-4 sm:p-5" data-testid="product-technical-data">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">How it plays</p>
                      <h3 className="mt-1 font-heading font-semibold text-lg text-[#111214]">{PADDLES[product.slug].identity}</h3>
                    </div>
                    <Link to="/compare" className={`inline-flex items-center gap-1.5 rounded-full ${theme.btn} text-white text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 hover:brightness-110 transition`} data-testid="product-compare-button">
                      Compare paddles <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                  <PlayProfile slug={product.slug} accent={theme.accent} />
                </div>
              )}
            </div>

            <Separator className="bg-[#E4E4E7]" />

            <div className="space-y-4">
              {!isLive ? (
                <div
                  className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-xl p-6 text-center"
                  data-testid="coming-soon-panel"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#111214] mb-3">
                    <Clock className={`w-6 h-6 ${theme.text}`} />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-[#111214] mb-1">Coming Soon</h3>
                  <p className="text-sm text-[#52525B] max-w-sm mx-auto">
                    The {product.name} is launching shortly. Drop your number and we'll WhatsApp you the moment it drops.
                  </p>
                  <div className="mt-5 text-left">
                    <NotifyMeForm slug={product.slug} launch />
                  </div>
                </div>
              ) : timer.counting ? (
                <LaunchCountdown variant="detail" slug={product.slug} />
              ) : product.in_stock === false && timer.launched ? (
                <LaunchedBanner slug={product.slug} />
              ) : product.in_stock === false ? (
                <NotifyMeForm slug={product.slug} />
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-[#52525B]">Quantity</span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="h-9 w-9 rounded border border-[#E4E4E7] text-[#111214] hover:bg-[#F4F4F5] flex items-center justify-center transition-colors"
                        data-testid="qty-decrement"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-center font-mono text-[#111214]" data-testid="qty-value">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity(quantity + 1)}
                        className="h-9 w-9 rounded border border-[#E4E4E7] text-[#111214] hover:bg-[#F4F4F5] flex items-center justify-center transition-colors"
                        data-testid="qty-increment"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <Button
                    ref={buyBtnRef}
                    onClick={() => addToCart(product, quantity)}
                    className={`w-full h-14 font-bold uppercase tracking-widest text-base flex items-center justify-center gap-2 ${theme.btn} text-white hover:bg-[#111214] transition-colors duration-300 rounded-lg`}
                    data-testid="add-to-cart-detail"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart — ₹{(product.priceINR * quantity).toLocaleString('en-IN')}
                  </Button>
                  <StickyBuyBar product={product} anchorRef={buyBtnRef} quantity={quantity} />
                  <AmazonBuyButton url={COMPANY.amazon[product.slug]} placement="product" className="mt-3" />
                  <TrustBadges className="mt-5" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Player reviews — honest & true, verified against orders, admin-moderated for spam only */}
        <ProductReviews slug={slug} reviews={reviews} aggregate={reviewAgg} />

        {/* FAQ — eligible for Google rich results & AI answers */}
        <div className="mt-10 max-w-3xl" data-testid="product-faq">
          <h2 className="font-display text-2xl md:text-3xl mb-6 text-[#111214]">Frequently asked questions</h2>
          <div className="divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
            {faqs.map((f, i) => (
              <details key={i} className="group py-4" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between text-base font-heading font-semibold text-[#111214]">
                  {f.q}
                  <Plus className="w-4 h-4 text-[#A1A1AA] group-open:rotate-45 transition-transform shrink-0 ml-3" />
                </summary>
                <p className="mt-3 text-sm text-[#52525B] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
